import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ArrowLeft as ArrowBackIcon,
  Save as SaveIcon,
} from 'lucide-react';
import { getStyleTemplate, saveStyleTemplate, updateStyleTemplate } from '../../utils/notesApi';

const PLACEHOLDER = `Write notes in a clinical but natural tone. Keep progression mild and believable from visit to visit.
Mention ongoing deficits while still showing functional improvement where appropriate.
Avoid robotic or generic phrasing. Keep plans concise and consistent.
Do not invent specific measurements or exact treatment minutes unless they are provided.`;

const StyleTemplatePage = () => {
  const [template, setTemplate] = useState(null);
  const [name, setName] = useState('Default Style Template');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStyleTemplate();
      setTemplate(res.data);
      setName(res.data.name);
      setText(res.data.template_text);
    } catch (err) {
      if (err.response?.status === 404) {
        // No template yet — show empty form
        setTemplate(null);
      } else {
        setError('Failed to load style template.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      if (template) {
        const res = await updateStyleTemplate(template.id, { name, template_text: text });
        setTemplate(res.data);
      } else {
        const res = await saveStyleTemplate({ name, template_text: text });
        setTemplate(res.data);
      }
      setSuccessMsg('Style template saved. It will be applied to future note generation.');
    } catch (err) {
      setError(err.response?.data?.template_text?.[0] || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton component={Link} to="/notes" size="small">
          <ArrowBackIcon size={16} />
        </IconButton>
        <Typography variant="h4">My Note Style Template</Typography>
        {template?.is_active && (
          <Chip label="Active" color="success" size="small" sx={{ ml: 1 }} />
        )}
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, ml: 5 }}>
        Describe once how you want your notes to sound. This template is automatically
        included in every note generation as style guidance — it does not override
        actual patient history.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <TextField
          label="Template Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
        />

        <TextField
          label="Style Instructions"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={8}
          fullWidth
          placeholder={PLACEHOLDER}
          helperText="Plain text. Describe your preferred tone, structure, and documentation habits."
          sx={{ mb: 3 }}
        />

        {template?.updated_at && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Last updated: {new Date(template.updated_at).toLocaleString()}
          </Typography>
        )}

        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon size={16} />}
          onClick={handleSave}
          disabled={saving || !text.trim()}
        >
          {template ? 'Save Changes' : 'Create Template'}
        </Button>
      </Paper>
    </Container>
  );
};

export default StyleTemplatePage;
